import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReservationWaitingModal from '../components/service/ReservationWaitingModal';
import HolidayWarningModal from '../components/service/HolidayWarningModal';
import { reservationService, waitlistService } from '../api/services';

/**
 * 예약 가용성 체크 훅
 * - check() : 가용성 확인 후 불가능하면 대기 모달 표시, boolean 반환
 * - modal   : 렌더링할 모달 JSX (없으면 null)
 */
export function useAvailabilityCheck({ serviceType, dateTime, passengers = 1, onSelectOtherDate }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showWaiting, setShowWaiting] = useState(false);
  const [registering, setRegistering] = useState(false);
  // 휴무 차단을 plain alert 가 아닌 HolidayWarningModal 로 통일.
  const [blockedHoliday, setBlockedHoliday] = useState(null);

  const check = useCallback(async () => {
    if (!dateTime) return true;
    const [date, time] = dateTime.split('T');
    try {
      const res = await reservationService.checkAvailability({
        service_type: serviceType,
        date,
        ...(time ? { start_time: time } : {}),
        passengers,
      });
      if (!res.available) {
        if (res.reason === 'HOLIDAY' || res.reason === 'BLOCKED_WINDOW') {
          setBlockedHoliday({ date, amount: 0, blocked: true });
          return false;
        }
        if (res.reason === 'NO_VEHICLE_FOR_PASSENGERS') {
          alert(t('booking.alert_vehicle_capacity_exceeded'));
          return false;
        }
        setShowWaiting(true);
        return false;
      }
      return true;
    } catch {
      return true;
    }
  }, [serviceType, dateTime, passengers, t]);

  const handleRegisterWaiting = async () => {
    const date = dateTime ? dateTime.split('T')[0] : '';
    const time = dateTime ? dateTime.split('T')[1] || '' : '';
    setRegistering(true);
    try {
      await waitlistService.create({
        service_type: serviceType,
        desired_date: date,
        desired_time: time,
        passengers,
      });
      setShowWaiting(false);
      // 대기 등록 완료 시 짧은 안내 후 메인으로 복귀 — 사용자가 흐름 종료를 명확히 인지하도록.
      // (이전엔 WaitlistCompleteModal 노출 후 페이지 머무름 → 사용자가 다음 단계 시도하다 이미 대기 등록됨 alert 만남)
      alert(t('booking.alert_waiting_complete'));
      navigate('/');
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || '';
      if (status === 409 || detail.includes('Already')) {
        alert(t('booking.alert_already_waiting'));
        setShowWaiting(false);
      } else {
        alert(t('booking.alert_waiting_failed'));
      }
    } finally {
      setRegistering(false);
    }
  };

  // BugHerd #4 (Vietnamese QA): 결제 직전 /api/reservations 에서 409 slot_conflict 가
  // 또 발생할 때 (buffer 겹침으로 가용성 1차 통과 후 BE 가 차단), alert 대신 동일 모달을
  // 재노출하기 위한 외부 트리거. 1차 흐름과 UX 동일하게.
  const showConflict = useCallback(() => setShowWaiting(true), []);

  const modal = blockedHoliday ? (
    <HolidayWarningModal
      holiday={blockedHoliday}
      onClose={() => { setBlockedHoliday(null); onSelectOtherDate?.(); }}
    />
  ) : showWaiting ? (
    <ReservationWaitingModal
      date={dateTime || ''}
      onClose={() => setShowWaiting(false)}
      onSelectOtherDate={() => { setShowWaiting(false); onSelectOtherDate?.(); }}
      onRegisterWaiting={handleRegisterWaiting}
      registering={registering}
    />
  ) : null;

  return { check, modal, showConflict };
}
